/**
 * What a deployment plan's state means to a person.
 *
 * The enum is printed raw today, in two places, and one of its members is
 * actively misleading. `EDITING` is the model default and NOTHING ever sets a
 * plan back to it — `run_deploy` only ever writes ACTIVE or ERROR — so it does
 * not mean "being edited", it means "has never been deployed". A badge reading
 * EDITING on a plan nobody has touched in months is worse than no badge.
 *
 * `INACTIVE` is written by nothing at all. Kept because the column can hold it
 * and a row that says INACTIVE has to render as something.
 *
 * Pure and dependency-free so jest can reach it, and shared so the plan card
 * and the plan header cannot describe the same state differently.
 */
import type { DeploymentPlanState } from '../../types';

export interface PlanStateBadge {
  label: string;
  /** Tailwind classes for the pill, light and dark. */
  className: string;
  /** Does this state mean work is happening right now? */
  busy?: boolean;
}

const NEVER_DEPLOYED: PlanStateBadge = {
  // NOT "Editing". See the module comment.
  label: 'Never deployed',
  className: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
};

const BADGES: Record<DeploymentPlanState, PlanStateBadge> = {
  EDITING: NEVER_DEPLOYED,
  ACTIVE: {
    // "Active" reads as a switch position. What it records is that a deploy ran
    // and did not fail.
    label: 'Deployed',
    className: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
  },
  INACTIVE: {
    label: 'Paused',
    className: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
  },
  ERROR: {
    // Names WHICH thing failed. "Error" on a plan reads as "this plan is
    // broken", when what happened is that one run of it did not finish.
    label: 'Last deploy failed',
    className: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
  },
  DEPLOYING: {
    label: 'Deploying…',
    className:
      'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 animate-pulse',
    busy: true,
  },
};

/** The badge for a plan's state. Null when there is no state to show. */
export function planStateBadge(
  state: DeploymentPlanState | null | undefined,
): PlanStateBadge | null {
  if (!state) return null;
  // A state the frontend has not learned about yet must render as itself rather
  // than vanish — a plan silently losing its badge hides the one case worth
  // seeing, which is a state we did not expect.
  return (
    BADGES[state] ?? {
      label: String(state),
      className: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    }
  );
}

/**
 * The line under a plan's name: what is waiting, what is live, how big it is.
 *
 * Returns null when the plan holds nothing at all — three zeroes read as broken
 * rather than as empty, and a brand-new plan is the most ordinary state there
 * is. The caller shows an invitation instead.
 *
 * `ready` and `live` are NOT a partition of `pages` and must not be presented as
 * one: a published page that has been rebuilt is both live and ready again, so
 * the two can sum past the total.
 */
export function planCountsLine(plan: {
  page_count?: number;
  ready_count?: number;
  live_count?: number;
}): string | null {
  const pages = plan.page_count ?? 0;
  if (pages === 0) return null;
  const ready = plan.ready_count ?? 0;
  const live = plan.live_count ?? 0;
  const parts = [
    ready > 0 ? `${ready} ready to deploy` : 'nothing waiting',
    `${live} live`,
    `${pages} page${pages === 1 ? '' : 's'}`,
  ];
  return parts.join(' · ');
}
